import React, { useEffect, useState } from 'react';
import { Table, TableProps, Button, Modal } from 'antd';
import { StopOutlined, DeleteOutlined } from '@ant-design/icons';
import { getRequest, postRequest, deleteRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import { compareVietnameseNames } from 'utils';

interface StudentData {
    studentId: number;
    studentClassYearId?: number;
    bhytNum: string;
    cmnd: string;
    gender: number;
    name: string;
    studentCode: string;
    dob: string;
    email: string;
    address: string;
    parentPhonenum: string;
}

interface ExpandedStudentListProps {
    classYearId: number;
    classTitle: string;
    onRefreshParent?: () => void;
}

export const ExpandedStudentList: React.FC<ExpandedStudentListProps> = ({ classYearId, classTitle, onRefreshParent }) => {
    const [students, setStudents] = useState<StudentData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<StudentData[]>([]);
    const [loadingBulkAction, setLoadingBulkAction] = useState<boolean>(false);

    useEffect(() => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        fetchStudentsInClass(classYearId);
    }, [classYearId]);

    const fetchStudentsInClass = async (id: number) => {
        setLoading(true);
        try {
            const response: ResponseDataType<{ list: StudentData[] }> = await getRequest(`/cms/student/get-students-by-class-year?classYearId=${id}&status=1`);
            if (response?.code === 200 && response.info && Array.isArray(response.info.list)) {
                setStudents(response.info.list);
            } else {
                setStudents([]);
            }
        } catch (error) {
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    const endStudentClassYear = async (studentId: number) => {
        // Gọi API lấy studentClassYearId
        const studentClassYearRes: ResponseDataType<number> = await getRequest(
            `/cms/class/get-student-class-year-by-student-and-class-year?classYearId=${classYearId}&studentId=${studentId}`
        );

        if (studentClassYearRes?.code === 200 && studentClassYearRes.info) {
            const studentClassYearId = studentClassYearRes.info;

            // Gọi API kết thúc năm học
            const endYearRes: ResponseDataType<any> = await postRequest(
                `/cms/class/student-end-class-year?studentClassYearIds=${studentClassYearId}`,
                {}
            );

            if (endYearRes?.code === 200) {
                toast.success('Kết thúc năm học thành công');
                fetchStudentsInClass(classYearId);
            } else {
                toast.error('Kết thúc năm học thất bại');
            }
        } else {
            toast.error('Không tìm thấy thông tin học sinh trong lớp');
        }
    };

    const handleDeleteStudent = async (studentId: number) => {
        const response: ResponseDataType<{}> = await deleteRequest(`/cms/class/${classYearId}/delete-studentClassYear`, { studentId });

        if (response?.code === 200) {
            toast.success('Xoá học sinh thành công');
            fetchStudentsInClass(classYearId);
        } else {
            if (response?.msg?.includes('income student register records')) {
                toast.error('Không thể xoá vì học sinh này vẫn còn các khoản thu trong năm học.');
            } else {
                toast.error(response?.msg || 'Xoá học sinh thất bại');
            }
        }
    };

    // Bulk action: Dừng học hàng loạt
    const handleBulkEndStudying = () => {
        if (!selectedRows.length) {
            toast.warning('Vui lòng chọn ít nhất một học sinh');
            return;
        }

        Modal.confirm({
            title: `Xác nhận dừng học cho ${selectedRows.length} học sinh đã chọn?`,
            content: 'Các học sinh này sẽ được chuyển sang trạng thái kết thúc năm học.',
            okText: 'Xác nhận',
            okType: 'primary',
            cancelText: 'Hủy',
            async onOk() {
                setLoadingBulkAction(true);
                try {
                    const ids: number[] = [];
                    for (const student of selectedRows) {
                        if (student.studentClassYearId) {
                            ids.push(student.studentClassYearId);
                        } else {
                            const res = await getRequest(
                                `/cms/class/get-student-class-year-by-student-and-class-year?classYearId=${classYearId}&studentId=${student.studentId}`
                            );
                            if (res?.code === 200 && res.info) {
                                ids.push(res.info);
                            }
                        }
                    }

                    if (!ids.length) {
                        toast.error('Không tìm thấy thông tin lớp năm học của học sinh');
                        setLoadingBulkAction(false);
                        return;
                    }

                    const endYearRes: ResponseDataType<any> = await postRequest(
                        `/cms/class/student-end-class-year?studentClassYearIds=${ids.join(',')}`,
                        {}
                    );

                    if (endYearRes?.code === 200) {
                        toast.success(`Đã dừng học thành công cho ${ids.length} học sinh!`);
                        setSelectedRowKeys([]);
                        setSelectedRows([]);
                        fetchStudentsInClass(classYearId);
                    } else {
                        toast.error(endYearRes?.msg || 'Dừng học thất bại');
                    }
                } catch (error) {
                    toast.error('Có lỗi xảy ra khi thực hiện dừng học');
                } finally {
                    setLoadingBulkAction(false);
                }
            },
        });
    };

    // Bulk action: Xóa khỏi lớp hàng loạt
    const handleBulkDeleteStudents = () => {
        if (!selectedRows.length) {
            toast.warning('Vui lòng chọn ít nhất một học sinh để xóa');
            return;
        }

        Modal.confirm({
            title: `Bạn có chắc chắn muốn xóa ${selectedRows.length} học sinh đã chọn khỏi lớp?`,
            content: 'Hành động này không thể hoàn tác.',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                setLoadingBulkAction(true);
                let successCount = 0;
                let failHasIncomeCount = 0;
                let failOtherCount = 0;

                for (const student of selectedRows) {
                    try {
                        const res: ResponseDataType<{}> = await deleteRequest(
                            `/cms/class/${classYearId}/delete-studentClassYear`,
                            { studentId: student.studentId }
                        );
                        if (res?.code === 200) {
                            successCount++;
                        } else if (res?.msg?.includes('income student register records')) {
                            failHasIncomeCount++;
                        } else {
                            failOtherCount++;
                        }
                    } catch (error) {
                        failOtherCount++;
                    }
                }

                if (successCount > 0) {
                    toast.success(`Đã xóa thành công ${successCount} học sinh khỏi lớp!`);
                }
                if (failHasIncomeCount > 0) {
                    toast.error(`${failHasIncomeCount} học sinh không thể xóa do còn nguồn thu/biên lai.`);
                }
                if (failOtherCount > 0) {
                    toast.error(`${failOtherCount} học sinh xóa thất bại.`);
                }

                setSelectedRowKeys([]);
                setSelectedRows([]);
                fetchStudentsInClass(classYearId);
                setLoadingBulkAction(false);
            },
        });
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: React.Key[], rows: StudentData[]) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
    };

    const columns: TableProps<StudentData>['columns'] = [
        { title: 'STT', key: 'stt', width: 45, align: 'center', render: (_, __, index) => index + 1 },
        { title: 'ID', dataIndex: 'studentId', key: 'studentId', width: 55, align: 'center' },
        {
            title: 'Họ tên',
            dataIndex: 'name',
            key: 'name',
            width: 160,
            align: 'left',
            render: (name) => <span style={{ fontWeight: 500 }}>{name}</span>,
            sorter: (a, b) => compareVietnameseNames(a.name, b.name),
            sortDirections: ['ascend', 'descend', 'ascend'],
        },
        { title: 'Ngày sinh', dataIndex: 'dob', key: 'dob', width: 95, align: 'center' },
        {
            title: 'Giới tính',
            dataIndex: 'gender',
            key: 'gender',
            width: 65,
            align: 'center',
            render: (gender) => (gender === 1 ? 'Nam' : 'Nữ'),
        },
        { title: 'Mã học sinh', dataIndex: 'studentCode', key: 'studentCode', width: 100, align: 'center' },
        { title: 'CCCD', dataIndex: 'cmnd', key: 'cmnd', width: 115, align: 'center' },
        { title: 'SĐT phụ huynh', dataIndex: 'parentPhonenum', key: 'parentPhonenum', width: 110, align: 'center' },
        {
            title: 'Hành động',
            key: 'action',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                    <span
                        style={{ color: '#fa8c16', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => {
                            Modal.confirm({
                                title: 'Bạn có chắc chắn muốn kết thúc năm học của học sinh này?',
                                okText: 'Xác nhận',
                                okType: 'primary',
                                cancelText: 'Hủy',
                                onOk: () => endStudentClassYear(record.studentId),
                            });
                        }}
                    >
                        Dừng học
                    </span>
                    <span
                        style={{ color: '#ff4d4f', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => {
                            Modal.confirm({
                                title: 'Bạn có chắc chắn muốn xoá học sinh này?',
                                okText: 'Xoá',
                                okType: 'danger',
                                cancelText: 'Hủy',
                                onOk: () => handleDeleteStudent(record.studentId),
                            });
                        }}
                    >
                        Xoá
                    </span>
                </div>
            ),
        },
    ];

    return (
        <div style={{ margin: '0', padding: '4px 8px', backgroundColor: '#fbfcfd', borderInlineStart: '3px solid #B9392E', borderBottom: '1px solid #e8e8e8' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '3px 8px',
                    marginBottom: '4px',
                    backgroundColor: '#f0f2f5',
                    borderRadius: '4px',
                    minHeight: '28px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#141D49' }}>
                        Danh sách học sinh lớp: <span style={{ color: '#B9392E' }}>{classTitle}</span>
                    </span>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                        (Sĩ số: {students.length} học sinh)
                    </span>
                </div>

                {selectedRowKeys.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 500, color: '#096dd9' }}>
                            Đã chọn <b>{selectedRowKeys.length}</b> học sinh
                        </span>
                        <Button
                            size="small"
                            icon={<StopOutlined />}
                            style={{ height: '22px', fontSize: '11.5px', padding: '0 8px', backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: '#fff', display: 'flex', alignItems: 'center' }}
                            onClick={handleBulkEndStudying}
                            loading={loadingBulkAction}
                        >
                            Dừng học ({selectedRowKeys.length})
                        </Button>
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            style={{ height: '22px', fontSize: '11.5px', padding: '0 8px', display: 'flex', alignItems: 'center' }}
                            onClick={handleBulkDeleteStudents}
                            loading={loadingBulkAction}
                        >
                            Xóa khỏi lớp ({selectedRowKeys.length})
                        </Button>
                        <span
                            style={{ fontSize: '12px', color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                                setSelectedRowKeys([]);
                                setSelectedRows([]);
                            }}
                        >
                            Bỏ chọn
                        </span>
                    </div>
                )}
            </div>

            <Table
                className="expanded-student-table"
                rowSelection={rowSelection}
                columns={columns}
                dataSource={students}
                rowKey="studentId"
                pagination={false}
                loading={loading}
                size="small"
                bordered
            />
        </div>
    );
};
